/**
 * What the Backlog lists once a project and a work type are chosen.
 *
 * The Backlog is grouped by work type, which suits ordering one kind of work for
 * the people doing it and does not answer "what is left on this project". Picking
 * a project adds an "All types" view, so every open step of it sits in one list.
 */
export const ALL_TYPES = "All" as const;

/**
 * The work-type buttons' selection after a click.
 *
 * A plain click picks that one type, or lets go of it to show every type again.
 * Ctrl/Cmd-click adds the type to the selection or takes it out. An empty set
 * means every type, which is how the Backlog always opens.
 */
export function toggleType(current: ReadonlySet<string>, type: string, additive: boolean): Set<string> {
  if (additive) {
    const next = new Set(current);
    if (!next.delete(type)) next.add(type);
    return next;
  }
  return current.size === 1 && current.has(type) ? new Set() : new Set([type]);
}

interface Row { assignment: { assignmentId: string; queuePosition: number | null }; phase: { projectId: string; workType: string; remainingHours: number; overEstimate?: boolean } }

/**
 * Steps with work left, or open and over their estimate, for the chosen work
 * types (none chosen is all of them), optionally within one project, in running order.
 */
export function backlogRows<T extends Row>(
  rows: readonly T[], types: ReadonlySet<string>, projectId: string, overOnly = false, inScope: (r: T) => boolean = () => true,
): T[] {
  return rows
    .filter((r) =>
      (overOnly ? r.phase.overEstimate === true : (r.phase.remainingHours > 0 || r.phase.overEstimate === true))
      && (types.size === 0 || types.has(r.phase.workType))
      && (!projectId || r.phase.projectId === projectId)
      && inScope(r))
    .sort((x, y) => (x.assignment.queuePosition ?? 0) - (y.assignment.queuePosition ?? 0));
}

/** The projects worth offering: those with at least one step that still has work left. */
export function projectsWithWork<P extends { projectId: string }>(
  projects: readonly P[], phases: readonly { projectId: string; remainingHours: number }[],
): P[] {
  const open = new Set(phases.filter((p) => p.remainingHours > 0).map((p) => p.projectId));
  return projects.filter((p) => open.has(p.projectId)).sort((a, b) => a.projectId.localeCompare(b.projectId));
}

const norm = (t: string | null | undefined) => (t ?? "").trim().toUpperCase();

/**
 * Whether a step belongs to a team's backlog.
 *
 * Work somebody holds belongs to the team that person is on. Work nobody holds
 * has no person to ask, so it belongs to the team that owns its project's type —
 * the same rule that gives a manager the unassigned work of their own types.
 * Without that, the one part of the backlog a manager most needs to find (what is
 * waiting for an owner) would drop out the moment a team is picked.
 *
 * A step that names a team is that team's, held or not; it also stays with the
 * team of whoever holds it. The rules above are for steps with no team yet.
 */
export function inTeamBacklog(
  teamId: string,
  team: { projectTypes?: string[] | null } | undefined,
  assignee: { teamId: string | null; isPool?: boolean } | undefined,
  productCode: string | null | undefined,
  stepTeamId?: string | null,
): boolean {
  if (!teamId) return true;
  if (stepTeamId) return stepTeamId === teamId || (!!assignee && !assignee.isPool && assignee.teamId === teamId);
  if (assignee && !assignee.isPool) return assignee.teamId === teamId;
  const code = norm(productCode);
  return !!code && (team?.projectTypes ?? []).some((t) => norm(t) === code);
}
